// src/pages/SubDealer/AddSubDealer.jsx
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import { useAuth } from '../../context/AuthContext';
import dealerService from '../../services/dealer.service';
import subDealerService from '../../services/subDealer.service';
import Toast from '../../components/Toast/Toast';
import '../../styles/AddPages.css';

const debounce = (fn, ms = 300) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    return new Promise(resolve => {
      timer = setTimeout(() => resolve(fn(...args)), ms);
    });
  };
};

const AddSubDealer = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const userRoleName = user?.role?.name || user?.role || '';
  const BLOCKED = ['FIELD_AGENT', 'GODOWN', 'ACCOUNTS'];
  const isBlocked = BLOCKED.includes(userRoleName);

  // ✅ ALL hooks first — no early returns above this
  const [formData, setFormData] = useState({
    dealerId: '',
    photo: null,
    latitude: '',
    longitude: '',
    subDealerShopName: '',
    ownerName: '',
    gstNumber: '',
    panNumber: '',
    aadharCardNumber: '',
    dateOfBirth: '',
    address: '',
    state: '',
    district: '',
    tahsil: '',
    mobile: '',
    email: '',
    remark: '',
  });

  const [parentDealerOption, setParentDealerOption] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [toast, setToast] = useState(null);

  // ✅ Redirect blocked roles — runs after hooks
  useEffect(() => {
    if (isBlocked) {
      navigate('/sub-dealer/view', { replace: true });
    }
  }, [isBlocked, navigate]);

  const resolveCompanyId = useCallback(() => {
    if (isSuperAdmin) {
      const cid = localStorage.getItem('selectedCompanyId');
      return cid ? parseInt(cid) : null;
    }
    return user?.companyId || null;
  }, [isSuperAdmin, user]);

  const loadDealers = useCallback(async (input) => {
    const cid = resolveCompanyId();
    if (isSuperAdmin && !cid) return [];
    try {
      const res = await dealerService.getAllDealers(cid, 1, 20, input || '');
      const list = res?.data?.dealers || [];
      return list.map(d => ({
        value: d.id,
        label: `${d.dealerShopName} — ${d.ownerName}${d.headquarter?.name ? ` (${d.headquarter.name})` : ''}`,
      }));
    } catch (err) {
      console.error('Dealer search error:', err);
      return [];
    }
  }, [isSuperAdmin, resolveCompanyId]);

  const debouncedLoadDealers = useCallback(debounce(loadDealers, 300), [loadDealers]);

  // ✅ NOW the conditional return — all hooks are above
  if (isBlocked) {
    return (
      <div className="add-dealer-page">
        <div style={{ padding: 40, textAlign: 'center' }}>
          <p style={{ color: '#e53e3e', fontSize: 16 }}>
            Your role is not authorized to add sub-dealers.
          </p>
        </div>
      </div>
    );
  }

  const handleParentDealerSelect = (option) => {
    setParentDealerOption(option);
    setFormData(prev => ({ ...prev, dealerId: option ? option.value : '' }));
    if (errors.dealerId) setErrors(prev => ({ ...prev, dealerId: '' }));
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      const file = files[0];
      setFormData(prev => ({ ...prev, [name]: file }));
      if (file) {
        const r = new FileReader();
        r.onloadend = () => setPhotoPreview(r.result);
        r.readAsDataURL(file);
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const detectLocation = () => {
  if (!navigator.geolocation) {
    setToast({ message: 'Geolocation is not supported by your browser.', type: 'error' });
    return;
  }

  setToast({ message: 'Detecting location…', type: 'info' });

  let bestReading = null;
  let settled = false;

  const finish = (position) => {
    if (settled) return;
    settled = true;
    navigator.geolocation.clearWatch(watchId);

    const { latitude, longitude, accuracy } = position.coords;
    setFormData(prev => ({
      ...prev,
      latitude: latitude.toFixed(8),
      longitude: longitude.toFixed(8),
      locationAccuracy: accuracy, // store this on the entity too
    }));

  setToast({ message: '📍 Location detected!', type: 'success' });
  };

  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      if (!bestReading || position.coords.accuracy < bestReading.coords.accuracy) {
        bestReading = position;
      }
      // good enough — stop early
      if (position.coords.accuracy <= 15) {
        finish(position);
      }
    },
    (error) => {
      if (settled) return;
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      const message =
        error.code === error.PERMISSION_DENIED
          ? 'Location access denied. Please enable it in your browser/app settings.'
          : error.code === error.TIMEOUT
          ? 'Location request timed out. Please try again.'
          : 'Unable to detect location. ';
      setToast({ message, type: 'error' });
      console.error('Geolocation error:', error);
    },
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
  );

  // Hard stop after ~12s — use best reading we've got rather than hang forever
  setTimeout(() => {
    if (!settled && bestReading) {
      finish(bestReading);
    } else if (!settled) {
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      setToast({ message: 'Could not get an accurate location. ', type: 'error' });
    }
  }, 12000);
};

  const validate = () => {
    const e = {};
    if (!formData.dealerId)              e.dealerId           = 'Parent dealer is required';
    if (!formData.subDealerShopName.trim()) e.subDealerShopName = 'Shop name is required';
    if (!formData.ownerName.trim())      e.ownerName          = 'Owner name is required';
    if (!formData.mobile.trim()) {
      e.mobile = 'Mobile is required';
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      e.mobile = 'Mobile must be 10 digits';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Invalid email';
    if (formData.gstNumber && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(formData.gstNumber))
      e.gstNumber = 'Invalid GST format';
    if (formData.panNumber && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber))
      e.panNumber = 'Invalid PAN format';
    if (formData.aadharCardNumber && !/^\d{12}$/.test(formData.aadharCardNumber))
      e.aadharCardNumber = 'Aadhar must be 12 digits';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const payload = { ...formData };
      if (formData.photo) payload.photo = photoPreview;
      else delete payload.photo;

      const res = await subDealerService.createSubDealer(payload);
      if (res.success) {
        setToast({ message: 'Sub-dealer added successfully!', type: 'success' });
        setTimeout(() => navigate('/sub-dealer/view'), 1200);
      }
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to add sub-dealer', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="add-sub-dealer-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/dashboard/overview">Dashboard</a></li>
          <li className="breadcrumb-item"><a href="/sub-dealer/view">Sub-Dealers</a></li>
          <li className="breadcrumb-item active">Add Sub-Dealer</li>
        </ol>
      </nav>

      <div className="page-header">
        <h4 className="page-title">Add New Sub-Dealer</h4>
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit}>

          {/* Parent Dealer */}
          <div className="form-section">
            <h3 className="section-title">🔗 Parent Dealer</h3>
            <div className="form-row">
              <div className="form-group form-group-full">
                <label className="form-label">
                  Select Parent Dealer <span className="required">*</span>
                </label>
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={debouncedLoadDealers}
                  value={parentDealerOption}
                  onChange={handleParentDealerSelect}
                  placeholder="Type to search dealer..."
                  noOptionsMessage={({ inputValue }) =>
                    inputValue ? 'No dealers match' : 'Start typing to search'}
                  isClearable
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    menu: (b) => ({ ...b, maxHeight: 240, overflowY: 'auto' }),
                  }}
                />
                {errors.dealerId && <span className="error-message">{errors.dealerId}</span>}
              </div>
            </div>
          </div>

          {/* Photo & Location */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Photo</label>
                <input type="file" name="photo" className="form-input"
                  onChange={handleChange} accept="image/*" />
                {photoPreview && (
                  <div className="photo-preview-large">
                    <img src={photoPreview} alt="Preview" />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Latitude</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input type="text" name="latitude" className="form-input"
                    value={formData.latitude} onChange={handleChange}
                    placeholder="Enter latitude" style={{ flex: 1 }} />
                  <button type="button" className="btn btn-secondary" onClick={detectLocation}>
                    📍 Detect
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Longitude</label>
                <input type="text" name="longitude" className="form-input"
                  value={formData.longitude} onChange={handleChange}
                  placeholder="Enter longitude" />
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Shop Name <span className="required">*</span></label>
                <input type="text" name="subDealerShopName"
                  className={`form-input ${errors.subDealerShopName ? 'error' : ''}`}
                  value={formData.subDealerShopName} onChange={handleChange}
                  placeholder="Sub-dealer shop name" />
                {errors.subDealerShopName && <span className="error-message">{errors.subDealerShopName}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Owner Name <span className="required">*</span></label>
                <input type="text" name="ownerName"
                  className={`form-input ${errors.ownerName ? 'error' : ''}`}
                  value={formData.ownerName} onChange={handleChange}
                  placeholder="Owner name" />
                {errors.ownerName && <span className="error-message">{errors.ownerName}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Date of Birth</label>
                <input type="date" name="dateOfBirth" className="form-input"
                  value={formData.dateOfBirth} onChange={handleChange} />
              </div>
            </div>
          </div>

          {/* Tax & Identity */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">GST Number</label>
                <input type="text" name="gstNumber"
                  className={`form-input ${errors.gstNumber ? 'error' : ''}`}
                  value={formData.gstNumber} onChange={handleChange}
                  placeholder="GST" maxLength="15" />
                {errors.gstNumber && <span className="error-message">{errors.gstNumber}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">PAN Number</label>
                <input type="text" name="panNumber"
                  className={`form-input ${errors.panNumber ? 'error' : ''}`}
                  value={formData.panNumber} onChange={handleChange}
                  placeholder="PAN" maxLength="10" />
                {errors.panNumber && <span className="error-message">{errors.panNumber}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Aadhar Number</label>
                <input type="text" name="aadharCardNumber"
                  className={`form-input ${errors.aadharCardNumber ? 'error' : ''}`}
                  value={formData.aadharCardNumber} onChange={handleChange}
                  placeholder="12-digit Aadhar" maxLength="12" />
                {errors.aadharCardNumber && <span className="error-message">{errors.aadharCardNumber}</span>}
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group form-group-full">
                <label className="form-label">Address</label>
                <textarea name="address" className="form-textarea"
                  value={formData.address} onChange={handleChange}
                  placeholder="Complete address" rows="3" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">State</label>
                <input type="text" name="state" className="form-input"
                  value={formData.state} onChange={handleChange} placeholder="State" />
              </div>
              <div className="form-group">
                <label className="form-label">District</label>
                <input type="text" name="district" className="form-input"
                  value={formData.district} onChange={handleChange} placeholder="District" />
              </div>
              <div className="form-group">
                <label className="form-label">Tahsil</label>
                <input type="text" name="tahsil" className="form-input"
                  value={formData.tahsil} onChange={handleChange} placeholder="Tahsil" />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className="form-section">
            <h3 className="section-title">Contact</h3>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Mobile <span className="required">*</span></label>
                <input type="tel" name="mobile" maxLength="10"
                  className={`form-input ${errors.mobile ? 'error' : ''}`}
                  value={formData.mobile} onChange={handleChange}
                  placeholder="10-digit mobile" />
                {errors.mobile && <span className="error-message">{errors.mobile}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input type="email" name="email"
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  value={formData.email} onChange={handleChange}
                  placeholder="Email" />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group form-group-full">
                <label className="form-label">Remark</label>
                <textarea name="remark" className="form-textarea"
                  value={formData.remark} onChange={handleChange}
                  placeholder="Additional remarks" rows="3" />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary"
              onClick={() => navigate('/sub-dealer/view')} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Adding...' : 'Add Sub-Dealer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSubDealer;